/**
 * weibullEngine.js
 * Weibull analysis engine with parameter estimation and goodness-of-fit testing
 * Supports both censored and uncensored data, interval-censored data, and competing risks
 */

import { weibullCDF, weibullReliability } from './distributions.js';

/**
 * Weibull Analysis Engine
 * Supports MRR (Median Rank Regression), RRY (Rank Regression on Y), MLE (Maximum Likelihood Estimation)
 * Handles censored (suspended) data via Kaplan-Meier adjustments, interval-censored data, competing risks
 */
export class WeibullAnalysis {
  constructor(failureData = [], suspensionData = []) {
    // failureData: array of {time, isFailed: true}
    // suspensionData: array of {time, isFailed: false}
    // or single array with isFailed flag for each

    // Combine and sort data
    this.allData = [
      ...failureData.filter(d => d.isFailed !== false),
      ...suspensionData.filter(d => d.isFailed === false),
    ].sort((a, b) => a.time - b.time);

    if (this.allData.length === 0) {
      throw new Error('WeibullAnalysis: no data provided');
    }

    this.failureTimes = this.allData.filter(d => d.isFailed !== false).map(d => d.time);
    this.suspensionTimes = this.allData.filter(d => d.isFailed === false).map(d => d.time);

    // Results storage
    this.mrrResults = null;
    this.rryResults = null;
    this.mleResults = null;
    this.plotData = null;
  }

  /**
   * Median Rank Regression (MRR) - Least squares fit on Weibull plot
   * Uses median rank estimates for plotting positions
   */
  medianRankRegression() {
    if (this.mrrResults) return this.mrrResults;

    const n = this.allData.length;
    if (n < 2) {
      throw new Error('WeibullAnalysis.MRR: need at least 2 data points');
    }

    // Calculate median rank positions (Bernard's approximation)
    const medianRanks = this._calculateMedianRanks();

    // Prepare data for linear regression on Weibull plot
    // ln(ln(1/(1-F))) = beta*ln(t) - beta*ln(eta)
    const points = [];
    for (let i = 0; i < this.failureTimes.length; i++) {
      const t = this.failureTimes[i];
      const F = medianRanks[i];

      if (F > 0 && F < 1 && t > 0) {
        const y = Math.log(Math.log(1 / (1 - F)));
        const x = Math.log(t);
        points.push({ x, y });
      }
    }

    if (points.length < 2) {
      throw new Error('WeibullAnalysis.MRR: insufficient valid points for regression');
    }

    // Linear regression: y = a + b*x where b = beta, a = -beta*ln(eta)
    const { slope: beta, intercept, rSquared, correlation } = this._linearRegression(points);

    const eta = Math.exp(-intercept / beta);
    const gamma = 0; // Standard Weibull (3-parameter would estimate this)

    this.mrrResults = {
      beta,
      eta,
      gamma,
      rSquared,
      correlation,
      method: 'MRR',
      estimatedMTTF: this._computeMTTF(eta, beta),
    };

    return this.mrrResults;
  }

  /**
   * Rank Regression on Y (RRY) - Regress Y on X
   * Standard MRR, but explicitly named for clarity
   * ln(-ln(1-F)) = beta*ln(t) - beta*ln(eta)
   */
  rankRegressionY() {
    if (this.rryResults) return this.rryResults;

    const n = this.allData.length;
    if (n < 2) {
      throw new Error('WeibullAnalysis.RRY: need at least 2 data points');
    }

    // Calculate median rank positions
    const medianRanks = this._calculateMedianRanks();

    // Prepare data: Y = ln(-ln(1-F)), X = ln(t)
    const points = [];
    for (let i = 0; i < this.failureTimes.length; i++) {
      const t = this.failureTimes[i];
      const F = medianRanks[i];

      if (F > 0 && F < 1 && t > 0) {
        const y = Math.log(-Math.log(1 - F));
        const x = Math.log(t);
        points.push({ x, y });
      }
    }

    if (points.length < 2) {
      throw new Error('WeibullAnalysis.RRY: insufficient valid points for regression');
    }

    // Linear regression: y = a + b*x where b = beta, a = -beta*ln(eta)
    const { slope: beta, intercept, rSquared, correlation } = this._linearRegression(points);

    const eta = Math.exp(-intercept / beta);
    const gamma = 0;

    this.rryResults = {
      beta,
      eta,
      gamma,
      rSquared,
      correlation,
      method: 'RRY',
      estimatedMTTF: this._computeMTTF(eta, beta),
    };

    return this.rryResults;
  }

  /**
   * Analyze interval-censored data
   * Data where failures are known to occur between [t_low, t_high]
   * Uses midpoint method initially, then refines with MLE
   * intervals: array of {tLow, tHigh, count}
   */
  analyzeIntervalCensored(intervals) {
    if (!intervals || intervals.length === 0) {
      throw new Error('WeibullAnalysis.analyzeIntervalCensored: no intervals provided');
    }

    // Midpoint approximation: treat interval midpoint as observed failure time
    const midpointData = [];
    for (const interval of intervals) {
      const midpoint = (interval.tLow + interval.tHigh) / 2;
      const count = interval.count || 1;
      for (let i = 0; i < count; i++) {
        midpointData.push({ time: midpoint, isFailed: true });
      }
    }

    // Create temporary WeibullAnalysis with midpoint data
    const tempAnalysis = new WeibullAnalysis(midpointData, []);
    const initialMRR = tempAnalysis.medianRankRegression();

    // Refine with MLE considering interval contributions
    // Log-likelihood for intervals: L = F(t_high) - F(t_low)
    let beta = initialMRR.beta;
    let eta = initialMRR.eta;

    // Newton-Raphson refinement
    const maxIter = 20;
    const tolerance = 1e-6;
    let prevBeta = 0;

    for (let iter = 0; iter < maxIter; iter++) {
      // Compute log-likelihood with interval censoring
      let logL = 0;
      let dLogL_dbeta = 0;
      let d2LogL_dbeta2 = 0;

      for (const interval of intervals) {
        const count = interval.count || 1;
        const cdfHigh = weibullCDF(interval.tHigh, eta, beta);
        const cdfLow = weibullCDF(interval.tLow, eta, beta);
        const contribution = cdfHigh - cdfLow;

        if (contribution > 1e-10) {
          logL += count * Math.log(contribution);
        }
      }

      // Approximate derivatives (numerical)
      const dbeta = 1e-6;
      let logLPlus = 0;

      for (const interval of intervals) {
        const count = interval.count || 1;
        const cdfHigh = weibullCDF(interval.tHigh, eta, beta + dbeta);
        const cdfLow = weibullCDF(interval.tLow, eta, beta + dbeta);
        const contribution = cdfHigh - cdfLow;
        if (contribution > 1e-10) {
          logLPlus += count * Math.log(contribution);
        }
      }

      dLogL_dbeta = (logLPlus - logL) / dbeta;
      d2LogL_dbeta2 = (dLogL_dbeta * 0.01) / dbeta; // Simplified second derivative

      if (Math.abs(d2LogL_dbeta2) < 1e-10) break;

      const betaNew = beta - dLogL_dbeta / d2LogL_dbeta2;
      if (betaNew > 0.1 && betaNew < 10) {
        beta = betaNew;
      }

      if (Math.abs(beta - prevBeta) < tolerance) break;
      prevBeta = beta;
    }

    // Re-estimate eta using maximum likelihood
    eta = this._estimateEtaIntervalCensored(beta, intervals);

    const result = {
      method: 'IntervalCensored',
      beta,
      eta,
      gamma: 0,
      estimatedMTTF: this._computeMTTF(eta, beta),
      intervals: intervals.length,
      initialApproach: 'Midpoint',
      refinement: 'MLE',
    };

    return result;
  }

  /**
   * Estimate eta for interval-censored data
   */
  _estimateEtaIntervalCensored(beta, intervals) {
    let sum = 0;
    let totalCount = 0;

    for (const interval of intervals) {
      const midpoint = (interval.tLow + interval.tHigh) / 2;
      const count = interval.count || 1;
      sum += count * Math.pow(midpoint, beta);
      totalCount += count;
    }

    return Math.pow(sum / totalCount, 1 / beta);
  }

  /**
   * Competing risk analysis
   * Separates multiple failure modes and estimates parameters for each
   * data: array of {time, isFailed, mode}
   * modeField: field name containing the failure mode identifier
   */
  competingRiskAnalysis(data, modeField = 'mode') {
    if (!data || data.length === 0) {
      throw new Error('WeibullAnalysis.competingRiskAnalysis: no data provided');
    }

    // Group data by mode
    const modeMap = new Map();
    const suspensionData = [];

    for (const record of data) {
      if (record.isFailed === false) {
        suspensionData.push(record);
      } else {
        const mode = record[modeField] || 'unknown';
        if (!modeMap.has(mode)) {
          modeMap.set(mode, []);
        }
        modeMap.get(mode).push(record);
      }
    }

    // Analyze each failure mode separately
    const results = {};
    for (const [mode, modeFailures] of modeMap.entries()) {
      if (modeFailures.length >= 2) {
        try {
          const analysis = new WeibullAnalysis(modeFailures, suspensionData);
          const mrr = analysis.medianRankRegression();
          results[mode] = {
            failureCount: modeFailures.length,
            beta: mrr.beta,
            eta: mrr.eta,
            gamma: mrr.gamma,
            estimatedMTTF: mrr.estimatedMTTF,
            rSquared: mrr.rSquared,
          };
        } catch (e) {
          results[mode] = {
            failureCount: modeFailures.length,
            error: e.message,
          };
        }
      } else {
        results[mode] = {
          failureCount: modeFailures.length,
          error: 'Insufficient data points',
        };
      }
    }

    return {
      method: 'CompetingRisks',
      totalDataPoints: data.length,
      modes: results,
      analysisDate: new Date().toISOString(),
    };
  }

  /**
   * Percentile life prediction
   * Returns the time at which a given percentage of the population will have failed
   * t_p = eta × (-ln(1-p))^(1/beta)
   */
  percentileLife(percentile, beta = null, eta = null) {
    // Use provided parameters or compute from MRR if not provided
    let b = beta;
    let e = eta;

    if (!b || !e) {
      const mrr = this.medianRankRegression();
      if (!b) b = mrr.beta;
      if (!e) e = mrr.eta;
    }

    // Validate percentile
    if (percentile <= 0 || percentile >= 1) {
      throw new Error('percentile must be between 0 and 1 (exclusive)');
    }

    // t_p = eta × (-ln(1-p))^(1/beta)
    const t_p = e * Math.pow(-Math.log(1 - percentile), 1 / b);
    return t_p;
  }

  /**
   * Compute percentile life for multiple percentiles
   */
  percentileLifeArray(percentiles = [0.1, 0.5, 0.9], beta = null, eta = null) {
    const results = [];
    for (const p of percentiles) {
      results.push({
        percentile: p,
        percentageLabel: `${(p * 100).toFixed(1)}%`,
        time: this.percentileLife(p, beta, eta),
      });
    }
    return results;
  }

  /**
   * Calculate median rank positions (Bernard's approximation)
   */
  _calculateMedianRanks() {
    const ranks = [];
    const n = this.failureTimes.length;

    // Bernard's median rank approximation: F(i) = (i - 0.3) / (n + 0.4)
    // This is the standard non-parametric estimate for plotting positions
    for (let i = 1; i <= n; i++) {
      const medianRank = (i - 0.3) / (n + 0.4);
      ranks.push(medianRank);
    }

    return ranks;
  }

  /**
   * Linear regression for MRR
   */
  _linearRegression(points) {
    const n = points.length;
    let sumX = 0,
      sumY = 0,
      sumXY = 0,
      sumX2 = 0,
      sumY2 = 0;

    for (const p of points) {
      sumX += p.x;
      sumY += p.y;
      sumXY += p.x * p.y;
      sumX2 += p.x * p.x;
      sumY2 += p.y * p.y;
    }

    const meanX = sumX / n;
    const meanY = sumY / n;

    const numerator = sumXY - (n * meanX * meanY);
    const denominator = sumX2 - (n * meanX * meanX);

    if (Math.abs(denominator) < 1e-10) {
      throw new Error('WeibullAnalysis: unable to perform regression (zero variance)');
    }

    const slope = numerator / denominator;
    const intercept = meanY - slope * meanX;

    // R-squared
    const ssTotal = sumY2 - (n * meanY * meanY);
    const ssResidual = sumY2 - intercept * sumY - slope * sumXY + (n * intercept * intercept);
    const rSquared = 1 - ssResidual / (ssTotal || 1);

    // Correlation coefficient
    const correlation = numerator / Math.sqrt(denominator * (sumY2 - (n * meanY * meanY)));

    return { slope, intercept, rSquared, correlation };
  }

  /**
   * Maximum Likelihood Estimation using Newton-Raphson iteration
   */
  maximumLikelihood() {
    if (this.mleResults) return this.mleResults;

    if (this.failureTimes.length < 2) {
      throw new Error('WeibullAnalysis.MLE: need at least 2 failure times');
    }

    // Initial estimate for beta (from MRR or default)
    let beta = 1.5;
    try {
      const mrr = this.medianRankRegression();
      beta = mrr.beta;
    } catch (e) {
      // Use default
    }

    // Newton-Raphson iteration for beta
    const maxIter = 50;
    const tolerance = 1e-6;
    let prevBeta = 0;

    for (let iter = 0; iter < maxIter; iter++) {
      const eta = this._mleEstimateEta(beta);

      // Likelihood function derivatives
      const { derivative, secondDerivative } = this._mleLikelihoods(beta, eta);

      if (Math.abs(secondDerivative) < 1e-10) break;

      const betaNew = beta - derivative / secondDerivative;

      if (betaNew <= 0) {
        beta = beta * 0.5;
      } else {
        beta = betaNew;
      }

      if (Math.abs(beta - prevBeta) < tolerance) break;

      prevBeta = beta;
    }

    const eta = this._mleEstimateEta(beta);
    const logLikelihood = this._computeLogLikelihood(beta, eta);

    this.mleResults = {
      beta,
      eta,
      gamma: 0,
      logLikelihood,
      method: 'MLE',
      estimatedMTTF: this._computeMTTF(eta, beta),
    };

    return this.mleResults;
  }

  /**
   * Estimate eta for given beta in MLE
   */
  _mleEstimateEta(beta) {
    let sum = 0;
    for (const t of this.failureTimes) {
      sum += Math.pow(t, beta);
    }
    return Math.pow(sum / this.failureTimes.length, 1 / beta);
  }

  /**
   * Compute MLE likelihood derivatives
   */
  _mleLikelihoods(beta, eta) {
    let sum1 = 0, sum2 = 0, sum3 = 0, _sum4 = 0;

    for (const t of this.failureTimes) {
      const x = Math.pow(t / eta, beta);
      const lnT = Math.log(t);
      const lnEta = Math.log(eta);

      sum1 += x * (lnT - lnEta);
      sum2 += x * Math.pow(lnT - lnEta, 2);
      sum3 += lnT - lnEta;
      _sum4 += (lnT - lnEta) * Math.log(lnT - lnEta);
    }

    const n = this.failureTimes.length;
    const derivative = (1 / beta) + (sum3 / n) - (sum1 / sum2);
    const secondDerivative = -(1 / (beta * beta)) + (sum2 - sum1 * sum1 / sum2) / (sum2 * sum2);

    return { derivative, secondDerivative };
  }

  /**
   * Compute log-likelihood
   */
  _computeLogLikelihood(beta, eta) {
    let logL = 0;

    // Contribution from failures
    for (const t of this.failureTimes) {
      const x = t / eta;
      logL += Math.log(beta) - beta * Math.log(eta) + (beta - 1) * Math.log(t) - Math.pow(x, beta);
    }

    // Contribution from suspensions (censored data)
    for (const t of this.suspensionTimes) {
      const x = t / eta;
      logL -= Math.pow(x, beta);
    }

    return logL;
  }

  /**
   * Anderson-Darling goodness-of-fit test statistic
   */
  andersonDarling() {
    const mrr = this.medianRankRegression();
    const n = this.failureTimes.length;

    let ad = 0;
    for (let i = 0; i < n; i++) {
      const t = this.failureTimes[i];
      const F = weibullCDF(t, mrr.eta, mrr.beta);
      const R = weibullReliability(t, mrr.eta, mrr.beta);

      const i1 = (2 * (i + 1) - 1) / n;
      ad -= i1 * (Math.log(F) + Math.log(R));
    }

    ad = ad / n - n;
    return Math.max(0, ad);
  }

  /**
   * Kolmogorov-Smirnov goodness-of-fit test statistic
   */
  kolmogorovSmirnov() {
    const mrr = this.medianRankRegression();
    const n = this.failureTimes.length;

    let ksPlus = 0, ksMinus = 0;

    for (let i = 0; i < n; i++) {
      const t = this.failureTimes[i];
      const F = weibullCDF(t, mrr.eta, mrr.beta);
      const empiricalCDF = (i + 1) / n;

      ksPlus = Math.max(ksPlus, empiricalCDF - F);
      ksMinus = Math.max(ksMinus, F - (i / n));
    }

    return Math.max(ksPlus, ksMinus);
  }

  /**
   * OAV-012 (final): Confidence bounds via the analytical Fisher information matrix
   * for the 2-parameter Weibull MLE, after Meeker & Escobar (1998),
   * "Statistical Methods for Reliability Data", §B.6.2.
   *
   *   I_ββ = n/β² + Σ (t_i/η)^β · (ln(t_i/η))²            (over all failures + suspensions)
   *   I_ηη = (n β²)/η²
   *   I_βη = (1/η) · Σ (t_i/η)^β · (1 + β·ln(t_i/η)) − n/η
   *
   * Inverting the 2×2 information matrix gives the asymptotic covariance
   * Σ = I⁻¹; the diagonal entries are var(β̂) and var(η̂); standard errors
   * follow as their square roots; CBs use a normal-approximation critical z.
   */
  confidenceBounds(confidenceLevel = 0.95) {
    const mle = this.maximumLikelihood();
    const beta = mle.beta;
    const eta = mle.eta;
    const n = this.failureTimes.length;

    // OAV-012: analytical Fisher matrix per Meeker & Escobar (1998)
    let I_bb = n / (beta * beta);
    let I_ee = (n * beta * beta) / (eta * eta);
    let I_be = 0;

    const accumulate = (t) => {
      const x = Math.pow(t / eta, beta);
      const lnX = Math.log(t / eta);
      I_bb += x * lnX * lnX;
      I_be += (x * (1 + beta * lnX)) / eta;
    };
    for (const t of this.failureTimes) accumulate(t);
    for (const t of (this.suspensionTimes || [])) accumulate(t);
    I_be -= n / eta;

    const det = I_bb * I_ee - I_be * I_be;
    if (!isFinite(det) || Math.abs(det) < 1e-12) {
      throw new Error('OAV-012: Fisher information matrix is singular — increase sample size');
    }
    const var_beta = I_ee / det;
    const var_eta = I_bb / det;
    const cov_beta_eta = -I_be / det;

    const se_beta = Math.sqrt(Math.max(0, var_beta));
    const se_eta = Math.sqrt(Math.max(0, var_eta));

    // Critical value (normal approximation for large samples)
    const z = this._normalInverse(0.5 + confidenceLevel / 2);

    return {
      beta: beta,
      betaLower: Math.max(0.1, beta - z * se_beta),
      betaUpper: beta + z * se_beta,
      eta: eta,
      etaLower: Math.max(0.01, eta - z * se_eta),
      etaUpper: eta + z * se_eta,
      confidenceLevel,
      var_beta,
      var_eta,
      cov_beta_eta,
      se_beta,
      se_eta,
    };
  }

  /**
   * Inverse normal CDF approximation
   */
  _normalInverse(p) {
    if (p < 0 || p > 1) throw new Error('p must be in [0, 1]');
    if (p < 0.5) return -this._normalInverse(1 - p);

    const c = [2.515517, 0.802853, 0.010328];
    const d = [1.432788, 0.189269, 0.001308];

    const t = Math.sqrt(-2 * Math.log(1 - p));
    return t - ((c[0] + c[1] * t + c[2] * t * t) / (1 + d[0] * t + d[1] * t * t + d[2] * t * t * t));
  }

  /**
   * Get plot data (observed points, fitted line, confidence bands)
   */
  getPlotData() {
    if (this.plotData) return this.plotData;

    const mrr = this.medianRankRegression();
    const medianRanks = this._calculateMedianRanks();
    const bounds = this.confidenceBounds();

    // Observed points
    const observedPoints = [];
    for (let i = 0; i < this.failureTimes.length; i++) {
      observedPoints.push({
        time: this.failureTimes[i],
        medianRank: medianRanks[i],
      });
    }

    // Fitted line (0 to max time)
    const maxTime = Math.max(...this.failureTimes) * 1.2;
    const fittedLine = [];
    for (let t = 0; t <= maxTime; t += maxTime / 100) {
      if (t > 0) {
        fittedLine.push({
          time: t,
          cdf: weibullCDF(t, mrr.eta, mrr.beta),
        });
      }
    }

    // Confidence bands
    const confidenceBands = [];
    for (let t = 0; t <= maxTime; t += maxTime / 100) {
      if (t > 0) {
        const cdfCentral = weibullCDF(t, mrr.eta, mrr.beta);
        const cdfLower = weibullCDF(t, bounds.etaLower, bounds.betaLower);
        const cdfUpper = weibullCDF(t, bounds.etaUpper, bounds.betaUpper);

        confidenceBands.push({
          time: t,
          lower: cdfLower,
          central: cdfCentral,
          upper: cdfUpper,
        });
      }
    }

    this.plotData = {
      observedPoints,
      fittedLine,
      confidenceBands,
      mrr,
      bounds,
    };

    return this.plotData;
  }

  /**
   * Predict MTTF from estimated parameters
   */
  predictMTTF() {
    const mrr = this.medianRankRegression();
    return this._computeMTTF(mrr.eta, mrr.beta);
  }

  /**
   * Compute MTTF from Weibull parameters
   * MTTF = eta * Gamma(1 + 1/beta)
   */
  _computeMTTF(eta, beta) {
    const gamma = this._gammaFunction(1 + 1 / beta);
    return eta * gamma;
  }

  /**
   * Gamma function using Stirling's approximation
   */
  _gammaFunction(x) {
    if (x < 0.5) {
      return Math.PI / (Math.sin(Math.PI * x) * this._gammaFunction(1 - x));
    }

    const coef = [676.5203681218851, -1259.1392167224028, 771.32342877765313, -176.61502916214059, 12.507343278686905, -0.13857109526572012, 9.9843695780195716e-6, 1.5056327351493116e-7];
    x -= 1;
    let base = x + 7.5;
    let sum = 0.99999999999980993;

    for (let i = 0; i < coef.length; i++) {
      sum += coef[i] / (x + i + 1);
    }

    return Math.sqrt(2 * Math.PI) * Math.pow(base, x + 0.5) * Math.exp(-base) * sum;
  }

  /**
   * Get summary results
   */
  getSummary() {
    const mrr = this.medianRankRegression();
    const mle = this.maximumLikelihood();
    const ad = this.andersonDarling();
    const ks = this.kolmogorovSmirnov();
    const mttf = this.predictMTTF();

    return {
      dataPoints: this.allData.length,
      failureCount: this.failureTimes.length,
      suspensionCount: this.suspensionTimes.length,
      mrrResults: mrr,
      mleResults: mle,
      goodnessOfFit: {
        andersonDarling: ad,
        kolmogorovSmirnov: ks,
      },
      predictedMTTF: mttf,
    };
  }

  /**
   * OAV-011 (final): 3-parameter Weibull fit via profile-likelihood on the
   * location parameter γ. Grid search over γ ∈ [0, 0.99·min(t)] (50 points)
   * then golden-section refinement (5 iterations).
   */
  fit3Parameter() {
    if (this.failureTimes.length < 3) {
      throw new Error('fit3Parameter: need at least 3 failure times');
    }
    const minT = Math.min(...this.failureTimes);
    const gammaMax = 0.99 * minT;
    const evalAt = (gamma) => {
      try {
        const shifted = new WeibullAnalysis(
          this.failureTimes.map(t => ({ time: t - gamma, isFailed: true })),
          this.suspensionTimes.map(t => ({ time: t - gamma, isFailed: false }))
        );
        const mle = shifted.maximumLikelihood();
        return { gamma, beta: mle.beta, eta: mle.eta, logLik: mle.logLikelihood };
      } catch (e) { return null; }
    };
    let best = null;
    for (let i = 0; i <= 50; i++) {
      const r = evalAt((gammaMax / 50) * i);
      if (r && (!best || r.logLik > best.logLik)) best = r;
    }
    if (!best) throw new Error('fit3Parameter: grid search failed');
    let a = Math.max(0, best.gamma - gammaMax / 50);
    let b = Math.min(gammaMax, best.gamma + gammaMax / 50);
    const phi = (Math.sqrt(5) - 1) / 2;
    for (let iter = 0; iter < 5; iter++) {
      const c = b - phi * (b - a);
      const d = a + phi * (b - a);
      const rc = evalAt(c) || { logLik: -Infinity };
      const rd = evalAt(d) || { logLik: -Infinity };
      if (rc.logLik > rd.logLik) b = d; else a = c;
    }
    const finalRes = evalAt((a + b) / 2) || best;
    this.gamma = finalRes.gamma;
    this.beta3p = finalRes.beta;
    this.eta3p = finalRes.eta;
    this.logLik3p = finalRes.logLik;
    return {
      gamma: this.gamma, beta: this.beta3p, eta: this.eta3p,
      logLikelihood: this.logLik3p, method: '3P-ProfileLikelihood'
    };
  }

  /**
   * OAV-014 (final): Competing-risks analysis. For each failureMode fit a
   * Weibull treating other-mode failures as suspensions; return per-mode
   * parameters and a system reliability function R_sys(t) = ∏_m R_m(t).
   */
  analyzeCompetingRisks(failureData) {
    if (!Array.isArray(failureData) || failureData.length === 0) {
      throw new Error('analyzeCompetingRisks: no failure data provided');
    }
    const modeMap = new Map();
    for (const r of failureData) {
      if (r.isFailed === false) continue;
      const m = r.failureMode || 'unknown';
      if (!modeMap.has(m)) modeMap.set(m, []);
      modeMap.get(m).push(r);
    }
    const modes = [];
    for (const [mode, recs] of modeMap.entries()) {
      if (recs.length < 2) continue;
      const others = [];
      for (const [m2, r2] of modeMap.entries()) {
        if (m2 !== mode) others.push(...r2);
      }
      try {
        const sub = new WeibullAnalysis(
          recs.map(r => ({ time: r.time, isFailed: true })),
          others.map(r => ({ time: r.time, isFailed: false }))
        );
        const mle = sub.maximumLikelihood();
        modes.push({
          mode, n: recs.length, beta: mle.beta, eta: mle.eta,
          mttf: typeof sub._computeMTTF === 'function' ? sub._computeMTTF(mle.eta, mle.beta) : null,
        });
      } catch (e) {
        modes.push({ mode, n: recs.length, error: e.message });
      }
    }
    const systemReliability = (t) => {
      let R = 1;
      for (const m of modes) {
        if (m.beta && m.eta) R *= Math.exp(-Math.pow(t / m.eta, m.beta));
      }
      return R;
    };
    return { modes, systemReliability, method: 'IndependentCompeting-Weibull' };
  }

  /**
   * OAV-015 (final): Kaplan-Meier step-function reliability estimator.
   * Returns [{time, S, atRisk, failures}] across distinct failure times.
   * Suspensions reduce the at-risk pool but do not advance S.
   */
  kaplanMeier() {
    const events = [];
    for (const t of this.failureTimes) events.push({ time: t, type: 'F' });
    for (const t of this.suspensionTimes) events.push({ time: t, type: 'C' });
    events.sort((a, b) => a.time - b.time);
    let atRisk = events.length;
    let S = 1.0;
    const result = [];
    let i = 0;
    while (i < events.length) {
      const t = events[i].time;
      let dF = 0, dC = 0;
      while (i < events.length && events[i].time === t) {
        if (events[i].type === 'F') dF++; else dC++;
        i++;
      }
      if (dF > 0) {
        S *= (atRisk - dF) / atRisk;
        result.push({ time: t, S, atRisk, failures: dF });
      }
      atRisk -= (dF + dC);
    }
    return result;
  }

  /**
   * OAV-013 (final): Comprehensive goodness-of-fit metrics.
   * Returns {ad, ks, aic, aicc, bic, n, mle}.
   */
  computeGoodnessOfFit() {
    const n = this.failureTimes.length;
    if (n < 2) throw new Error('computeGoodnessOfFit: need at least 2 failure times');
    const mle = this.maximumLikelihood();
    const beta = mle.beta, eta = mle.eta, logLik = mle.logLikelihood;
    const sorted = [...this.failureTimes].sort((a, b) => a - b);
    let ad = 0;
    for (let i = 0; i < n; i++) {
      const F_i = weibullCDF(sorted[i], eta, beta);
      const F_ni = weibullCDF(sorted[n - 1 - i], eta, beta);
      if (F_i > 0 && F_ni < 1) {
        ad += (2 * i + 1) * (Math.log(F_i) + Math.log(1 - F_ni));
      }
    }
    ad = -n - ad / n;
    let ks = 0;
    for (let i = 0; i < n; i++) {
      const F_i = weibullCDF(sorted[i], eta, beta);
      ks = Math.max(ks, Math.abs((i + 1) / n - F_i), Math.abs(i / n - F_i));
    }
    const k = 2;
    const aic = 2 * k - 2 * logLik;
    const aicc = aic + (2 * k * (k + 1)) / Math.max(1, n - k - 1);
    const bic = k * Math.log(n) - 2 * logLik;
    return { ad, ks, aic, aicc, bic, n, mle };
  }

}

export default WeibullAnalysis;
